import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { ArrowLeft, Download, WhatsappLogo, Copy, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_FILTERS = [
  { label: '1D', days: 1 },
  { label: '7D', days: 7 },
  { label: '15D', days: 15 },
  { label: '30D', days: 30 },
];

const InactiveClientsTracker = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('dws_user') || '{}');
  const isAdmin = user.role === 'super_admin';

  const [clients, setClients] = useState(() => readInstantCache('inactive_clients', 'all') || []);
  const [loading, setLoading] = useState(() => !readInstantCache('inactive_clients', 'all'));
  const [selectedDays, setSelectedDays] = useState(7);
  const [customDays, setCustomDays] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchInactiveClients = useCallback(async () => {
    if (!readInstantCache('inactive_clients', 'all')) setLoading(true);
    try {
      const response = await axios.get(`${API}/clients/inactive`, {
        params: { user_id: user.id, role: user.role, days: selectedDays }
      });
      const _v = response.data || [];
      setClients(_v);
      writeInstantCache('inactive_clients', 'all', _v);
    } catch (error) {
      console.error('Failed to fetch inactive clients:', error);
      toast.error('Failed to load inactive clients');
    } finally {
      setLoading(false);
    }
  }, [selectedDays, user.id, user.role]);

  useEffect(() => {
    if (user.id) fetchInactiveClients();
  }, [fetchInactiveClients, user.id]);

  const handlePresetFilter = (days) => {
    setShowCustom(false);
    setSelectedDays(days);
  };

  const applyCustomDays = () => {
    const d = parseInt(customDays);
    if (d > 0) {
      setSelectedDays(d);
      setShowCustom(false);
    } else {
      toast.error('Enter valid number of days');
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/clients/inactive/export-pdf`, {
        params: { days: selectedDays, role: user.role, user_id: user.id },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inactive_clients_${selectedDays}days.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = async () => {
    if (!clients.length) { toast.error('No data to export'); return; }
    setExporting(true);
    try {
      const response = await axios.get(`${API}/clients/inactive/export-excel`, {
        params: { days: selectedDays, role: user.role, user_id: user.id },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `client_activity_${selectedDays}days.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded!');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error('Excel export failed');
    } finally {
      setExporting(false);
    }
  };

  const copyClientInfo = (client) => {
    const info = [
      client.username,
      client.phone ? `Phone: ${client.phone}` : '',
      client.bank_name ? `Bank: ${client.bank_name}` : '',
      client.account_number ? `A/C: ${client.account_number}` : '',
      client.ifsc_code ? `IFSC: ${client.ifsc_code}` : '',
      client.upi_id ? `UPI: ${client.upi_id}` : '',
      client.address ? `Address: ${client.address}` : '',
      `Inactive: ${client.inactive_days} days`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(info);
    toast.success('Copied!');
  };

  const openWhatsApp = (phone) => {
    if (!phone) return;
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/91${cleanPhone}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: '2-digit' });
    } catch { return '-'; }
  };

  const filteredClients = clients.filter(c =>
    c.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone?.includes(searchQuery)
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white p-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="back-btn">
            <ArrowLeft size={22} weight="bold" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold">Inactive Clients</h1>
            <p className="text-xs text-white/80">{selectedDays}+ days | {filteredClients.length} clients</p>
          </div>
          {isAdmin && (
          <button
            onClick={exportPDF}
            disabled={exporting || clients.length === 0}
            data-testid="download-pdf-btn"
            className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <Download size={16} weight="bold" />
            {exporting ? '...' : 'PDF'}
          </button>
          )}
          {isAdmin && (
          <button
            onClick={exportExcel}
            disabled={clients.length === 0}
            data-testid="download-excel-btn"
            className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <Download size={16} weight="bold" />
            Excel
          </button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b px-3 py-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex gap-1" data-testid="filter-tabs">
            {PRESET_FILTERS.map(f => (
              <button
                key={f.days}
                onClick={() => handlePresetFilter(f.days)}
                data-testid={`filter-${f.days}`}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  selectedDays === f.days && !showCustom
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => setShowCustom(!showCustom)}
              data-testid="filter-custom"
              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                showCustom ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <CalendarBlank size={12} /> Custom
            </button>
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-inactive"
            className="flex-1 px-3 py-1 border rounded-lg text-xs focus:ring-1 focus:ring-red-400 focus:outline-none min-w-0"
          />
        </div>
        {showCustom && (
          <div className="flex gap-2 mt-2" data-testid="custom-days-input">
            <input
              type="number"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              placeholder="Enter days"
              min="1"
              className="flex-1 px-3 py-1 border rounded-lg text-xs"
            />
            <button onClick={applyCustomDays} className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs font-medium">
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: 'touch', ...(!isAdmin ? { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' } : {}) }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500"></div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="font-medium">No inactive clients</p>
            <p className="text-sm mt-1">for {selectedDays}+ days</p>
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="inactive-table">
            <thead className="bg-slate-800 sticky top-0 z-10 shadow">
              <tr className="text-left text-white font-bold uppercase text-[10px] tracking-wide">
                <th className="px-2 py-2.5 whitespace-nowrap">#</th>
                <th className="px-2 py-2.5 whitespace-nowrap">Username</th>
                <th className="px-2 py-2.5 whitespace-nowrap">Client ID</th>
                <th className="px-2 py-2.5 whitespace-nowrap">Mobile</th>
                <th className="px-2 py-2.5 whitespace-nowrap">Address</th>
                <th className="px-2 py-2.5 whitespace-nowrap">📍 IFSC Address</th>
                <th className="px-2 py-2.5 whitespace-nowrap">Last Login</th>
                <th className="px-2 py-2.5 whitespace-nowrap text-center">Inactive Days</th>
                <th className="px-2 py-2.5 whitespace-nowrap text-right">Last Withdrawal</th>
                <th className="px-2 py-2.5 whitespace-nowrap text-right">Last Deposit</th>
                <th className="px-2 py-2.5 whitespace-nowrap text-center">WA</th>
                {isAdmin && <th className="px-2 py-2.5 whitespace-nowrap text-center">Copy</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredClients.map((client, idx) => {
                const wAmt = client.last_withdraw_amount;
                const dAmt = client.last_deposit_amount;
                const fmtAmt = (v) => (v === null || v === undefined || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);
                return (
                <tr key={client.id} className={`${idx % 2 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50/40`} data-testid={`inactive-row-${idx}`}>
                  <td className="px-2 py-2.5 text-gray-400 font-mono">{idx + 1}</td>
                  <td className="px-2 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{client.username || '-'}</td>
                  <td className="px-2 py-2.5 font-mono text-indigo-700 whitespace-nowrap">{client.client_id || '-'}</td>
                  <td className="px-2 py-2.5 whitespace-nowrap text-gray-700">{client.phone || '-'}</td>
                  <td className="px-2 py-2.5 text-gray-500 max-w-[160px] truncate" title={client.address || ''}>{client.address || ''}</td>
                  <td
                    className="px-2 py-2.5 text-blue-700 max-w-[180px] truncate"
                    title={client.client_address?.formatted || client.client_address?.address || ''}
                    data-testid={`inactive-ifsc-address-${client.id}`}
                  >{client.client_address_text || client.client_address?.formatted || ''}</td>
                  <td className="px-2 py-2.5 whitespace-nowrap text-gray-500">{formatDate(client.last_login_at || client.last_activity)}</td>
                  <td className="px-2 py-2.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      client.inactive_days >= 30 ? 'bg-red-100 text-red-700' :
                      client.inactive_days >= 15 ? 'bg-orange-100 text-orange-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>{client.inactive_days}d</span>
                  </td>
                  <td className="px-2 py-2.5 text-right whitespace-nowrap font-semibold text-orange-700">{fmtAmt(wAmt)}</td>
                  <td className="px-2 py-2.5 text-right whitespace-nowrap font-semibold text-emerald-700">{fmtAmt(dAmt)}</td>
                  <td className="px-2 py-2.5 text-center">
                    <button onClick={() => openWhatsApp(client.phone)} className="text-green-600 hover:text-green-700" data-testid={`wa-btn-${idx}`}>
                      <WhatsappLogo size={18} weight="fill" />
                    </button>
                  </td>
                  {isAdmin && (
                    <td className="px-2 py-2.5 text-center">
                      <button onClick={() => copyClientInfo(client)} className="text-blue-600 hover:text-blue-700" data-testid={`copy-btn-${idx}`}>
                        <Copy size={18} />
                      </button>
                    </td>
                  )}
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InactiveClientsTracker;
